import React, { useCallback, useMemo } from 'react';

import { FormOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { MY_ASSETS } from '@/shared/lib/constants';
import type { AssetListAction, MyAssetItem } from '@/shared/types/assetList';
import { AssetList } from '@/shared/ui/AssetList';
import { NoDataBoundary } from '@/shared/ui/NoDataBoundary';

import type { MyAsset } from '@/entities/asset';

import { useMyAssets } from '@/features/my-assets';

import styles from './MyAssetsTab.module.css';

dayjs.extend(relativeTime);

type MyAssetsTabProps = {
  onAssetClick: (asset: MyAssetItem) => void;
};

/**
 * MyAsset을 MyAssetItem으로 변환합니다
 */
const mapToAssetListItem = (asset: MyAsset): MyAssetItem => ({
  id: asset.id,
  name: asset.name,
  type: asset.type,
  fqn: asset.fqn,
  updatedAt: asset.updatedAt || '알수 없음',
});

/**
 * 상대 시간 포맷을 반환합니다
 */
const getRelativeTime = (item: MyAssetItem): string => {
  return dayjs(item.updatedAt).fromNow();
};

/**
 * 최근 변경된 자산 목록 탭 컴포넌트
 *
 * @description
 * - 무한 스크롤을 통해 페이지네이션을 구현합니다
 * - 스크롤이 바닥에 가까워지면 자동으로 다음 페이지를 로드합니다
 * - 기존 AssetList 컴포넌트 구조를 유지합니다
 */
export const MyAssetsTab: React.FC<MyAssetsTabProps> = ({ onAssetClick }) => {
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } = useMyAssets();

  const allMyAssets = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data?.pages]);

  const myAssets: MyAssetItem[] = useMemo(() => allMyAssets.map(mapToAssetListItem), [allMyAssets]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - MY_ASSETS.SCROLL_THRESHOLD;

      if (isNearBottom && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  const getActions = useCallback((): AssetListAction[] => [], []);

  return (
    <div className={styles.tabWrapper}>
      <NoDataBoundary
        data={myAssets}
        emptyDescription="최근 변경된 자산이 없습니다"
        emptyProps={{
          description: (
            <div>
              <div className={styles.emptyTitle}>최근 변경된 자산이 없습니다</div>
              <div className={styles.emptyDescription}>새로운 데이터 자산을 생성해보세요</div>
            </div>
          ),
        }}>
        <div className={styles.tabContent}>
          <div className={styles.listContainer} onScroll={handleScroll}>
            <AssetList
              items={myAssets}
              getActions={getActions}
              getMetadata={getRelativeTime}
              metadataIcon={<FormOutlined size={14} />}
              onItemClick={onAssetClick}
            />
            {isFetchingNextPage && <div className={styles.loadingMore}>추가 데이터를 불러오는 중...</div>}
          </div>
        </div>
      </NoDataBoundary>
    </div>
  );
};
